import React from 'react';
import { ArrowLeft, Play, Power, Trash2 } from 'lucide-react';
import { AutomationStatus } from '@frontend/app/types/globalTypes';
import { EllipsisIcon } from '@revfluence/fresh-icons/regular';
import HeaderButton from '@frontend/app/refresh-components/HeaderButton';
import Tag from '@frontend/app/refresh-components/Tag';
import { determineAutomationStatus } from '@frontend/applications/AffiliatesApp/components/EmailAutomations/EmailAutomations';
import { Button } from '@/shadcn/components/ui/button';
import { Badge } from '@/shadcn/components/ui/badge';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
} from '@/shadcn/components/ui/breadcrumb';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shadcn/components/ui/dropdown-menu';

interface AutomationHeaderProps {
  // Breadcrumb configuration
  breadcrumbs?: Array<{
    label: string;
    href?: string;
    onClick?: () => void;
  }>;
  currentPage?: string;

  // Back button
  onCancel?: () => void;
  redirectUrl?: string;

  // Title and status
  automationName: string;
  automation?: any;
  hasUnsavedChanges?: boolean;

  // Mode toggle
  readOnly?: boolean;
  isEditing?: boolean;
  onToggleEdit?: () => void;

  // Save functionality
  onSave?: () => void;
  isSaving?: boolean;
  automationId?: string;

  // Dropdown actions
  onDisable?: () => void;
  onResume?: () => void;
  onDelete?: () => void;

  // History for navigation
  history?: any;
}

export const AutomationHeader: React.FC<AutomationHeaderProps> = ({
  breadcrumbs = [],
  currentPage,
  onCancel,
  redirectUrl,
  automationName,
  automation,
  hasUnsavedChanges = false,
  readOnly = false,
  isEditing = false,
  onToggleEdit,
  onSave,
  isSaving = false,
  automationId,
  onDisable,
  onResume,
  onDelete,
  history,
}) => {
  const handleBackClick = () => {
    if (redirectUrl && history) {
      history.push(redirectUrl);
    } else if (onCancel) {
      onCancel();
    }
  };

  return (
    <div className="bg-primary border-b border-grey-2 px-6 py-4 flex-shrink-0">
      <div className="w-full">
        {/* Top row with breadcrumb */}
        <div className="flex items-center justify-between w-full mb-2">
          <Breadcrumb>
            <BreadcrumbList className="text-xs gap-1">
              {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={index}>
                  {index !== 0 && <BreadcrumbSeparator className="text-grey-4 [&>svg]:w-3 [&>svg]:h-3" />}
                  <BreadcrumbItem>
                    <BreadcrumbLink
                      href={crumb.href || '#'}
                      onClick={crumb.onClick}
                      className="text-xs text-grey-4 hover:text-grey-0"
                    >
                      {crumb.label}
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                </React.Fragment>
              ))}
              <BreadcrumbSeparator className="text-grey-4 [&>svg]:w-3 [&>svg]:h-3" />
              <BreadcrumbItem>
                <BreadcrumbLink
                  href="#"
                  className="text-xs text-grey-0"
                >
                  {currentPage || (automationId ? 'Edit Automation' : 'Create Automation')}
                </BreadcrumbLink>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        {/* Bottom row with back button, title, and actions */}
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center space-x-3">
            {onCancel && (
              <HeaderButton
                onClick={handleBackClick}
                icon={<ArrowLeft className="h-4 w-4" />}
              />
            )}
            <div className="flex items-center gap-4">
              <h1 className="text-grey-0 font-medium text-lg m-0 leading-none">
                {automationName}
              </h1>
              {automation && (
                <Tag
                  key="approved"
                  label={determineAutomationStatus(automation)}
                  bgColor={automation.status === AutomationStatus.ACTIVE ? 'bg-green-4' : 'bg-yellow-4'}
                  textColor="text-primary"
                />
              )}
              {hasUnsavedChanges && (
                <Badge variant="outline" className="text-orange-6 border-orange-6">
                  Unsaved Changes
                </Badge>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {!readOnly && (
              <>
                <p className="text-xs text-grey-0 m-0">{automation?.updatedDate ? `Last Updated ${new Date(automation?.updatedDate).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}` : ''}</p>

                {/* Automation Actions Dropdown - only show when editing existing automation */}
                {automationId && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outlineHeader" size="icon" className="h-8 w-8">
                        <EllipsisIcon className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={onDisable}
                        disabled={automation?.status === AutomationStatus.DISABLED}
                      >
                        <Power className="mr-2 h-4 w-4" />
                        Turn Off
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={onResume}
                        disabled={automation?.status === AutomationStatus.ACTIVE}
                      >
                        <Play className="mr-2 h-4 w-4" />
                        Turn On
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={onDelete}
                        className="text-red-600 focus:text-red-600"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete Automation
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

                <Button
                  variant="outlineHeader"
                  size="sm"
                  onClick={onToggleEdit}
                  disabled={isSaving}
                >
                  {isEditing ? 'View Mode' : 'Edit Mode'}
                </Button>

                <Button
                  onClick={onSave}
                  disabled={!hasUnsavedChanges || isSaving}
                  variant="outline"
                  size="sm"
                  tooltip={isSaving
                    ? 'Saving automation changes...'
                    : !hasUnsavedChanges
                      ? 'No changes to save'
                      : automationId
                        ? 'Save changes to automation'
                        : 'Create new automation'}
                >
                  {isSaving ? 'Saving...' : automationId ? 'Update Automation' : 'Create Automation'}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
